import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AppLayout from '@/components/feature/AppLayout';
import { useToast } from '@/contexts/ToastContext';
import { financeApi, isDocumentVoucher, type JournalVoucher } from '@/api/financeApi';
import { formatINR } from '@/utils/format';

export default function JVDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();

  const [loading, setLoading] = useState(true);
  const [jv, setJv] = useState<JournalVoucher | null>(null);
  const [reversing, setReversing] = useState(false);
  const [posting, setPosting] = useState(false);

  const loadVoucher = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      const data = await financeApi.getJournalVoucherById(id);
      setJv(data);
    } catch (err: any) {
      console.error('Failed to load voucher:', err);
      toast.error(err.response?.data?.message || 'Journal voucher not found');
    } finally {
      setLoading(false);
    }
  }, [id, toast]);

  useEffect(() => {
    loadVoucher();
  }, [loadVoucher]);

  if (loading) {
    return (
      <AppLayout>
        <div className="py-24 text-center text-slate-400 flex flex-col items-center gap-2">
          <i className="ri-loader-4-line animate-spin text-3xl text-primary-500" />
          <p className="text-xs">Loading voucher details...</p>
        </div>
      </AppLayout>
    );
  }

  if (!jv) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <i className="ri-file-search-line text-4xl text-slate-300 mb-3 block" />
            <h2 className="text-lg font-bold text-slate-800">Journal Voucher Not Found</h2>
            <p className="text-sm text-slate-500 mt-1">JV with ID "{id}" does not exist</p>
            <button
              onClick={() => navigate('/finance/journal')}
              className="mt-4 h-9 px-4 rounded-lg bg-[#4f46e5] text-white text-sm font-medium hover:bg-indigo-700 cursor-pointer transition-colors shadow-sm"
            >
              Back to Journal List
            </button>
          </div>
        </div>
      </AppLayout>
    );
  }

  const statusColors: Record<string, string> = {
    DRAFT: 'bg-amber-100 text-amber-800 border-amber-200',
    POSTED: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    CANCELLED: 'bg-rose-100 text-rose-800 border-rose-200',
    REVERSED: 'bg-purple-100 text-purple-800 border-purple-200',
  };

  const handlePost = async () => {
    try {
      setPosting(true);
      await financeApi.postJournalVoucher(jv.id);
      toast.success(`${jv.voucherNumber} posted to General Ledger`);
      loadVoucher();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to post voucher');
    } finally {
      setPosting(false);
    }
  };

  const handleReverse = async () => {
    const reason = prompt(`Enter reason for reversing ${jv.voucherNumber}:`, 'Voucher adjustment / correction');
    if (reason === null) return;
    try {
      setReversing(true);
      const rev = await financeApi.reverseJournalVoucher(jv.id, { reason });
      toast.success(`Voucher reversed successfully. Generated ${rev.voucherNumber}.`);
      loadVoucher();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to reverse voucher');
    } finally {
      setReversing(false);
    }
  };

  const handlePrint = () => {
    const printContent = `
      <html>
      <head>
        <title>${jv.voucherNumber}</title>
        <style>
          body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; padding: 40px; color: #0f172a; }
          .header { text-align: center; border-bottom: 2px solid #0f172a; padding-bottom: 12px; margin-bottom: 20px; }
          .company { font-size: 20px; font-weight: bold; }
          .title { font-size: 14px; text-transform: uppercase; letter-spacing: 1px; color: #64748b; margin-top: 4px; }
          .info { display: flex; justify-content: space-between; margin-bottom: 20px; font-size: 13px; }
          table { width: 100%; border-collapse: collapse; margin-top: 10px; }
          th { text-align: left; padding: 8px; border-bottom: 2px solid #cbd5e1; font-size: 11px; text-transform: uppercase; color: #475569; }
          td { padding: 8px; border-bottom: 1px solid #f1f5f9; font-size: 12px; }
          .dr { text-align: right; font-family: monospace; }
          .cr { text-align: right; font-family: monospace; }
          .total { font-weight: bold; border-top: 2px solid #0f172a; }
          .footer { margin-top: 60px; display: flex; justify-content: space-between; }
          .sign-box { text-align: center; border-top: 1px solid #94a3b8; padding-top: 8px; width: 180px; font-size: 11px; color: #475569; }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="company">DIGI INDIA SOLUTIONS / INVENPRO ERP</div>
          <div class="title">JOURNAL VOUCHER (${jv.status})</div>
        </div>
        <div class="info">
          <div>
            <strong>Voucher Number:</strong> ${jv.voucherNumber}<br/>
            <strong>Type:</strong> ${jv.voucherType}
          </div>
          <div style="text-align: right;">
            <strong>Date:</strong> ${new Date(jv.voucherDate + 'T00:00:00').toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}<br/>
            <strong>Status:</strong> ${jv.status}
          </div>
        </div>
        <table>
          <thead>
            <tr>
              <th style="width: 15%;">Account Code</th>
              <th style="width: 45%;">Account / Particulars</th>
              <th class="dr" style="width: 20%;">Debit (₹)</th>
              <th class="cr" style="width: 20%;">Credit (₹)</th>
            </tr>
          </thead>
          <tbody>
            ${(jv.entries || [])
              .map(
                (e) => `
              <tr>
                <td style="font-family: monospace;">${e.accountCode || ''}</td>
                <td>
                  <strong>${e.accountName || ''}</strong>
                  ${e.partyName ? `<br/><small style="color:#2563eb;">Party: ${e.partyName}</small>` : ''}
                  ${e.narration ? `<br/><small style="color:#64748b;">${e.narration}</small>` : ''}
                </td>
                <td class="dr">${e.debit > 0 ? formatINR(e.debit) : '—'}</td>
                <td class="cr">${e.credit > 0 ? formatINR(e.credit) : '—'}</td>
              </tr>
            `
              )
              .join('')}
            <tr class="total">
              <td colspan="2">TOTAL</td>
              <td class="dr">${formatINR(jv.totalDebit)}</td>
              <td class="cr">${formatINR(jv.totalCredit)}</td>
            </tr>
          </tbody>
        </table>
        <p style="margin-top: 24px; font-size: 12px; color: #334155;">
          <strong>Narration:</strong> ${jv.narration || 'N/A'}
        </p>
        <div class="footer">
          <div class="sign-box">Prepared By</div>
          <div class="sign-box">Checked By</div>
          <div class="sign-box">Authorised Signatory</div>
        </div>
      </body>
      </html>
    `;
    const w = window.open('', '_blank');
    if (w) {
      w.document.write(printContent);
      w.document.close();
      w.print();
    }
  };

  const formatDate = (d: string) => {
    if (!d) return '—';
    const dt = new Date(d.includes('T') ? d : d + 'T00:00:00');
    return dt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  return (
    <AppLayout>
      <div className="flex flex-col h-full bg-[#f8fafc]">
        <div className="flex-1 overflow-y-auto pb-16">
          {/* Header */}
          <div className="flex items-center justify-between px-6 pt-6">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/finance/journal')}
                className="w-8 h-8 rounded-lg border border-[#e2e8f0] flex items-center justify-center text-slate-600 hover:bg-slate-100 transition-colors"
              >
                <i className="ri-arrow-left-line text-sm" />
              </button>
              <div>
                <div className="flex items-center gap-2">
                  <h1 className="text-xl font-bold font-mono text-[#1e293b]">{jv.voucherNumber}</h1>
                  <span className={`text-xs font-bold px-2.5 py-0.5 rounded-full border ${statusColors[jv.status]}`}>
                    {jv.status}
                  </span>
                  {jv.isReversed && (
                    <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-purple-100 text-purple-800 border border-purple-200">
                      Reversed
                    </span>
                  )}
                </div>
                <p className="text-sm text-slate-500 mt-0.5">
                  Voucher Date: {formatDate(jv.voucherDate)} · Type: {jv.voucherType}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={handlePrint}
                className="flex items-center gap-1.5 h-9 px-3.5 rounded-lg border border-[#e2e8f0] bg-white text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors shadow-sm cursor-pointer"
              >
                <i className="ri-printer-line text-sm text-slate-500" /> Print
              </button>

              {jv.status === 'DRAFT' && (
                <button
                  disabled={posting}
                  onClick={handlePost}
                  className="flex items-center gap-1.5 h-9 px-4 rounded-lg bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 transition-colors shadow-sm cursor-pointer"
                >
                  <i className="ri-check-line text-sm" /> {posting ? 'Posting...' : 'Post to Ledger'}
                </button>
              )}

              {jv.status === 'POSTED' && isDocumentVoucher(jv) && (
                <span
                  className="flex items-center gap-1.5 h-9 px-3 rounded-lg bg-slate-100 text-slate-600 text-xs font-medium border border-[#e2e8f0]"
                  title="This voucher is generated from its source document and changes with it"
                >
                  <i className="ri-link text-sm" /> Auto-posted from {jv.referenceTable?.replace(/_/g, ' ')}
                </span>
              )}

              {jv.status === 'POSTED' && !jv.isReversed && !isDocumentVoucher(jv) && (
                <button
                  disabled={reversing}
                  onClick={handleReverse}
                  className="flex items-center gap-1.5 h-9 px-4 rounded-lg bg-purple-600 text-white text-sm font-medium hover:bg-purple-700 transition-colors shadow-sm cursor-pointer"
                >
                  <i className="ri-arrow-go-back-line text-sm" /> {reversing ? 'Reversing...' : 'Reverse Voucher'}
                </button>
              )}
            </div>
          </div>

          <div className="px-6 py-5 max-w-5xl mx-auto space-y-4">
            {/* Voucher Meta Card */}
            <div className="bg-white border border-[#e2e8f0] rounded-xl p-5 shadow-sm grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <p className="text-xs text-slate-500 font-medium">Voucher Number</p>
                <p className="text-sm font-bold font-mono text-primary-700 mt-0.5">{jv.voucherNumber}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 font-medium">Entry Date</p>
                <p className="text-sm font-semibold text-slate-800 mt-0.5">{formatDate(jv.voucherDate)}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 font-medium">Reference</p>
                <p className="text-sm font-semibold text-slate-800 mt-0.5">
                  {jv.referenceTable ? `${jv.referenceTable} / ${jv.referenceId?.slice(0, 8)}` : 'Manual Entry'}
                </p>
              </div>
              <div>
                <p className="text-xs text-slate-500 font-medium">Posted At</p>
                <p className="text-sm font-semibold text-slate-800 mt-0.5">
                  {jv.postedAt ? new Date(jv.postedAt).toLocaleString('en-IN') : 'Not posted yet'}
                </p>
              </div>
              {jv.narration && (
                <div className="md:col-span-4 pt-2 border-t border-slate-100">
                  <p className="text-xs text-slate-500 font-medium">Narration</p>
                  <p className="text-sm text-slate-800 mt-0.5">{jv.narration}</p>
                </div>
              )}
            </div>

            {/* Entries Table */}
            <div className="bg-white border border-[#e2e8f0] rounded-xl overflow-hidden shadow-sm">
              <div className="px-5 py-3 border-b border-[#e2e8f0] bg-slate-100/50">
                <h3 className="text-xs font-bold text-slate-800 uppercase tracking-wider">
                  Journal Line Items ({(jv.entries || []).length})
                </h3>
              </div>
              <table className="w-full text-xs">
                <thead className="bg-slate-50 border-b border-slate-100 text-[10px] text-slate-400 uppercase tracking-wider font-semibold">
                  <tr>
                    <th className="text-left px-5 py-3 w-28">Account Code</th>
                    <th className="text-left px-4 py-3">Account Name</th>
                    <th className="text-left px-4 py-3 w-32">Party / Sub-Ledger</th>
                    <th className="text-right px-4 py-3 w-36">Debit (Dr)</th>
                    <th className="text-right px-4 py-3 w-36">Credit (Cr)</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {(jv.entries || []).map((entry, idx) => (
                    <tr key={idx} className="hover:bg-slate-50/50">
                      <td className="px-5 py-3 font-mono font-medium text-slate-600">{entry.accountCode}</td>
                      <td className="px-4 py-3">
                        <span className="font-semibold text-[#1e293b]">{entry.accountName}</span>
                        {entry.narration && (
                          <p className="text-[11px] text-slate-500 mt-0.5">{entry.narration}</p>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {entry.partyName ? (
                          <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-blue-50 text-blue-700 border border-blue-200">
                            {entry.partyName}
                          </span>
                        ) : (
                          <span className="text-slate-300">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right font-mono font-bold text-[#1e293b]">
                        {entry.debit > 0 ? formatINR(entry.debit) : '—'}
                      </td>
                      <td className="px-4 py-3 text-right font-mono font-bold text-[#1e293b]">
                        {entry.credit > 0 ? formatINR(entry.credit) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-slate-100/60 font-bold border-t-2 border-[#e2e8f0]">
                  <tr>
                    <td colSpan={3} className="px-5 py-3 text-right uppercase tracking-wider text-xs text-slate-700">
                      Total
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-sm text-[#1e293b]">
                      {formatINR(jv.totalDebit)}
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-sm text-[#1e293b]">
                      {formatINR(jv.totalCredit)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}